import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtRp } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const TYPES = [["aset", "Aset"], ["kewajiban", "Kewajiban"], ["ekuitas", "Ekuitas"], ["pendapatan", "Pendapatan"], ["beban", "Beban"]];
const empty = { code: "", name: "", type: "aset", normal: "", description: "" };

export function AccountsTab() {
  const { isOwner } = useAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/accounting/accounts").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((p) => ({ ...p, data: { ...p.data, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form.data, normal: form.data.normal || null };
    try {
      if (form.item) await api.put(`/accounting/accounts/${form.item.id}`, payload); else await api.post("/accounting/accounts", payload);
      toast.success("Akun disimpan"); setForm(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Hapus akun ${a.code} ${a.name}?`)) return;
    try { await api.delete(`/accounting/accounts/${a.id}`); toast.success("Akun dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="acc-coa">
      <div className="flex justify-end items-center gap-3 mb-4 rise rise-1">
        {isOwner ? <Button className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setForm({ item: null, data: empty })} data-testid="btn-add-account"><Plus className="h-4 w-4 mr-1" /> Tambah Akun</Button>
          : <span className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="coa-owner-only"><Lock className="h-3 w-3" /> Perubahan jenis akun hanya oleh akun utama (Pemilik)</span>}
      </div>
      <div className="space-y-4">
        {TYPES.map(([t, label]) => {
          const rows = list.filter((a) => a.type === t);
          if (!rows.length) return null;
          return (
            <div key={t} className="card-aqua overflow-hidden rise rise-2" data-testid={`coa-group-${t}`}>
              <div className="px-5 py-3 border-b border-sky-100 flex justify-between items-center"><span className="font-bold text-slate-800">{label}</span><span className="text-xs text-slate-500">{rows.length} akun</span></div>
              <table className="w-full text-sm">
                <thead className="table-head"><tr><th className="text-left px-4 py-2 w-28">Kode</th><th className="text-left px-4 py-2">Nama Akun</th><th className="text-left px-4 py-2">Saldo Normal</th><th className="text-right px-4 py-2">Saldo Saat Ini</th><th className="text-right px-4 py-2 w-28">Aksi</th></tr></thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} className="border-t border-sky-50 hover:bg-sky-50/50" data-testid={`coa-row-${a.code}`}>
                      <td className="px-4 py-2.5 font-mono text-xs font-semibold text-sky-800">{a.code}</td>
                      <td className="px-4 py-2.5 text-slate-800 font-medium">{a.name}{a.is_system && <Lock className="inline h-3 w-3 ml-1.5 text-slate-300" />}{a.description && <div className="text-xs text-slate-400">{a.description}</div>}</td>
                      <td className="px-4 py-2.5 text-slate-600 capitalize">{a.normal}</td>
                      <td className={`px-4 py-2.5 text-right font-mono font-semibold ${a.balance < 0 ? "text-rose-600" : ""}`}>{fmtRp(a.balance)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {isOwner && <div className="inline-flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-sky-700" onClick={() => setForm({ item: a, data: { code: a.code, name: a.name, type: a.type, normal: a.normal, description: a.description || "" } })} data-testid={`btn-edit-account-${a.code}`}><Pencil className="h-4 w-4" /></Button>
                          {!a.is_system && <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => remove(a)} data-testid={`btn-delete-account-${a.code}`}><Trash2 className="h-4 w-4" /></Button>}
                        </div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="account-form-dialog">
          <DialogHeader><DialogTitle>{form?.item ? "Edit Akun" : "Tambah Akun"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Kode</Label><Input value={form.data.code} onChange={(e) => set("code", e.target.value)} required placeholder="5-2600" data-testid="input-account-code" /></div>
                <div className="space-y-1.5 col-span-2"><Label>Nama Akun</Label><Input value={form.data.name} onChange={(e) => set("name", e.target.value)} required placeholder="cth. Beban Es Balok" data-testid="input-account-name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tipe</Label>
                  <Select value={form.data.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger data-testid="select-account-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Saldo Normal</Label>
                  <Select value={form.data.normal || "auto"} onValueChange={(v) => set("normal", v === "auto" ? "" : v)}>
                    <SelectTrigger data-testid="select-account-normal"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="auto">Otomatis (sesuai tipe)</SelectItem><SelectItem value="debit">Debit</SelectItem><SelectItem value="credit">Kredit</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Deskripsi</Label><Input value={form.data.description} onChange={(e) => set("description", e.target.value)} placeholder="Opsional" data-testid="input-account-desc" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForm(null)}>Batal</Button>
                <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-account-submit">{saving ? "Menyimpan..." : "Simpan"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
